/**
 * CSTR dinamik rejim — RK4 (rk4Step, ichki qadamlar avtomatik).
 *
 * HOLAT: {C_A, C_B, T, t}. Tenglamalar balances.ts sarlavhasida.
 * Boshlang'ich holat: reaktor kirish aralashmasi bilan to'ldirilgan (C_A0, C_B0, Tin).
 *
 * ICHKI QADAM (mahalliy-adaptiv): har ichki qadamda joriy holat bo'yicha eng kichik
 * vaqt doimiysining 0.25 qismi (qadam ichida k(T) o'nlab barobar oshishi mumkin):
 *   τ_res = V/F,  τ_rxn = 1/(n·k·C_A^(n−1)),  τ_heat = ρcpV/(Fρcp + UA),
 *   τ_exo = ρcp·R·T²/((−ΔH)·r·Ea)  (harorat bo'yicha teskari aloqa vaqti).
 * Ichki qadam dt/2000 dan kichik bo'lmaydi; har ichki qadamdan keyin
 * C_A, C_B ≥ 0 proyeksiyasi (qochib ketish holatida qattiq tizim RK4 uchun
 * beqaror bo'lishi mumkin — proyeksiya natijani chegaralab turadi, aniqlik
 * pasayadi, lekin xato tashlanmaydi; `reactor.runaway` critical beriladi).
 *
 * ASSUMPTION: isothermal rejimda T = Tin doimiy (dT/dt = 0).
 *
 * MANBA: Fogler ECRE 13-bob (CSTR ishga tushirish, unsteady operation).
 */
import { R_GAS } from '../../properties';
import { rk4Step } from '../../solvers';
import { warn, type StepResult, type Warning } from '../../types';
import { arrhenius, mixtureProperties, rateDerivative, reactionRate } from '../reaction';
import { heatTransferUA, validateCstrInputs, validateCstrParams } from './balances';
import { outputsFor } from './steady';
import type { CstrInputs, CstrParams, CstrState, CstrStepOutputs } from './types';

const MAX_SUBSTEPS = 2000;
const SAFETY = 0.25;

export function initState(p: CstrParams, inputs: CstrInputs): CstrState {
  validateCstrParams(p);
  validateCstrInputs(p, inputs);
  return { t: 0, CA: inputs.CA0, CB: inputs.CB0 ?? 0, T: inputs.Tin };
}

export function stepDynamic(
  p: CstrParams,
  state: CstrState,
  inputs: CstrInputs,
  dt: number,
): StepResult<CstrState, CstrStepOutputs> {
  validateCstrInputs(p, inputs);
  if (!(dt > 0)) throw new Error('cstr: dt must be > 0');
  const warnings: Warning[] = [];
  const mix = mixtureProperties(p.reaction, inputs.Tin, warnings);
  const { flowRate: F, CA0, Tin } = inputs;
  const CB0 = inputs.CB0 ?? 0;
  const V = p.volume;
  const UA = heatTransferUA(p);
  const Tj = p.mode === 'jacketed' ? (inputs.Tjacket as number) : Tin;
  const { k0, Ea } = p.reaction;
  const n = mix.order;
  const isothermal = p.mode === 'isothermal';

  if (F <= 0) warn(warnings, 'reactor.noFlow', 'critical');

  const deriv = (_t: number, y: readonly number[]): number[] => {
    const CA = y[0] as number;
    const CB = y[1] as number;
    const T = y[2] as number;
    const r = reactionRate(arrhenius(k0, Ea, T), CA, n);
    const dCA = (F / V) * (CA0 - CA) - r;
    const dCB = (F / V) * (CB0 - CB) + mix.yieldB * r;
    const dT = isothermal
      ? 0
      : ((F / V) * (Tin - T) * mix.rhoCp + mix.heatRelease * r - (UA * (T - Tj)) / V) / mix.rhoCp;
    return [dCA, dCB, dT];
  };

  // Joriy holatdagi eng kichik vaqt doimiysi (ichki qadam uchun)
  const localTau = (CA: number, T: number): number => {
    const k = arrhenius(k0, Ea, T);
    let tauMin = F > 0 ? V / F : Infinity;
    const drdC = rateDerivative(k, CA, n);
    if (drdC > 0) tauMin = Math.min(tauMin, 1 / drdC);
    if (!isothermal) {
      const denom = F * mix.rhoCp + UA;
      if (denom > 0) tauMin = Math.min(tauMin, (mix.rhoCp * V) / denom);
      const exo = Math.abs(mix.heatRelease * reactionRate(k, CA, n) * Ea);
      if (exo > 0) tauMin = Math.min(tauMin, (mix.rhoCp * R_GAS * T * T) / exo);
    }
    return tauMin;
  };

  // Mahalliy-adaptiv ichki qadam: h = SAFETY·τ_min(joriy holat), h ≥ dt/MAX_SUBSTEPS
  const hMin = dt / MAX_SUBSTEPS;
  let y = [state.CA, state.CB, state.T];
  let t = state.t;
  let remaining = dt;
  let count = 0;
  while (remaining > 1e-12 * dt && count < MAX_SUBSTEPS) {
    const h = Math.min(
      remaining,
      Math.max(hMin, SAFETY * localTau(y[0] as number, y[2] as number)),
    );
    y = rk4Step(deriv, t, y, h);
    t += h;
    remaining -= h;
    count++;
    y[0] = Math.max(0, y[0] as number);
    y[1] = Math.max(0, y[1] as number);
    if (isothermal) y[2] = Tin;
  }

  const next: CstrState = {
    t: state.t + dt,
    CA: y[0] as number,
    CB: y[1] as number,
    T: y[2] as number,
  };
  if (!Number.isFinite(next.T) || !Number.isFinite(next.CA)) {
    throw new Error('cstr: non-finite state (unstable step)');
  }

  if (p.Tmax !== undefined && next.T > p.Tmax) {
    warn(warnings, 'reactor.runaway', 'critical', { T: Number(next.T.toFixed(1)), Tmax: p.Tmax });
  }

  const out = outputsFor(p, inputs, mix, next.CA, next.T);
  // Dinamikada C_B holatdan olinadi (kirish/chiqish balansidan emas)
  return { state: next, outputs: { ...out, CB: next.CB, t: next.t }, warnings };
}
